import { useEffect, useMemo } from 'react';

function upsertMeta(attribute, value, content) {
  if (typeof document === 'undefined' || !value) return;
  let element = document.head.querySelector(`meta[${attribute}="${value}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    element.setAttribute('data-vn-seo', 'true');
    document.head.appendChild(element);
  }
  element.setAttribute('content', content || '');
}

function upsertLink(rel, href) {
  if (typeof document === 'undefined' || !rel) return;
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    element.setAttribute('data-vn-seo', 'true');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href || '');
}

function replaceJsonLdScripts(entries) {
  if (typeof document === 'undefined') return;
  document
    .querySelectorAll('script[data-vn-seo-jsonld="true"]')
    .forEach((node) => node.parentNode?.removeChild(node));

  entries.forEach((entry) => {
    if (!entry) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-vn-seo-jsonld', 'true');
    script.text = JSON.stringify(entry);
    document.head.appendChild(script);
  });
}

export default function SEOHead({
  title,
  description,
  canonical,
  jsonLd,
  noindex = false,
}) {
  const jsonLdEntries = useMemo(() => {
    if (!jsonLd) return [];
    return Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : [jsonLd];
  }, [jsonLd]);

  const jsonLdKey = useMemo(() => JSON.stringify(jsonLdEntries), [jsonLdEntries]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (title) {
      document.title = title;
      upsertMeta('property', 'og:title', title);
      upsertMeta('name', 'twitter:title', title);
    }

    if (description) {
      upsertMeta('name', 'description', description);
      upsertMeta('property', 'og:description', description);
      upsertMeta('name', 'twitter:description', description);
    }

    if (canonical) {
      upsertLink('canonical', canonical);
      upsertMeta('property', 'og:url', canonical);
    }

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow');

    replaceJsonLdScripts(jsonLdEntries);
  }, [canonical, description, jsonLdEntries, jsonLdKey, noindex, title]);

  return null;
}
